import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import isFunction from 'lodash/isFunction'
import { useController, useFormContext } from 'react-hook-form'
import { buildLegacyFieldValidator } from './legacyFieldValidation'
import { getLegacyDisplayedError } from './fieldErrorDisplay'
import type { InternationalStrings } from '../StaticText/StaticTextContext'
import type {
  LegacyFieldValidate,
  LegacyValidationProps,
} from './formFieldTypes'

export type UseLegacyFieldValidatorOptions = LegacyValidationProps

export function useLegacyFieldValidator (
  staticText: InternationalStrings,
  {
    validations,
    required,
    validationErrors,
    validationError,
    validationErrorFormat,
  }: UseLegacyFieldValidatorOptions,
): LegacyFieldValidate {
  const requiredMessage = staticText.getIn(['data', 'required'])

  return useMemo(
    () => buildLegacyFieldValidator({
      validations,
      required,
      validationErrors,
      validationError,
      validationErrorFormat,
      requiredMessage: requiredMessage != null ? String(requiredMessage) : undefined,
    }),
    [
      validations,
      required,
      validationErrors,
      validationError,
      validationErrorFormat,
      requiredMessage,
    ],
  )
}

export type UseLegacyFormFieldOptions<TValue> = LegacyValidationProps & {
  name: string
  defaultValue: TValue
  staticText: InternationalStrings
  errorOnPristine?: boolean
  validationTimeout?: number
  validate?: LegacyFieldValidate
  value?: TValue
  onChange?: (...args: unknown[]) => unknown
}

export function useLegacyFormField<TValue> ({
  name,
  defaultValue,
  staticText,
  required,
  validations,
  validationErrors,
  validationError,
  validationErrorFormat,
  forceError,
  errorOnPristine = false,
  validationTimeout = 1,
  validate,
  value: valueProp,
  onChange: onChangeProp,
}: UseLegacyFormFieldOptions<TValue>) {
  const { control, setValue } = useFormContext()
  const builtValidate = useLegacyFieldValidator(staticText, {
    validations,
    required,
    validationErrors,
    validationError,
    validationErrorFormat,
  })
  const resolvedValidate = validate || builtValidate

  const {
    field,
    fieldState,
  } = useController({
    name,
    control,
    rules: { validate: resolvedValidate },
    defaultValue,
  })

  const externallyControlled = isFunction(onChangeProp)
  const [showError, setShowError] = useState(errorOnPristine)
  const [clientValidationMessage, setClientValidationMessage] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const displayValue = externallyControlled && valueProp !== undefined
    ? valueProp
    : field.value

  useEffect(() => {
    if (externallyControlled && valueProp !== undefined && valueProp !== field.value) {
      setValue(name, valueProp, { shouldValidate: true })
    }
  }, [externallyControlled, valueProp, field.value, name, setValue])

  const scheduleShowError = useCallback((shouldShow: boolean) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (shouldShow) {
      if (validationTimeout <= 1) {
        setShowError(true)
        return
      }
      timerRef.current = setTimeout(() => {
        setShowError(true)
        timerRef.current = null
      }, validationTimeout)
    } else {
      setShowError(false)
    }
  }, [validationTimeout])

  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
  }, [])

  const getDisplayedError = useCallback(
    () => {
      const fieldErrorMessage = fieldState.error?.message || clientValidationMessage || undefined
      const shouldShowFieldError = showError
        || Boolean(fieldErrorMessage && fieldState.isDirty)

      return getLegacyDisplayedError({
        validationErrors,
        validationError,
        validationErrorFormat,
        forceError,
        showError: shouldShowFieldError,
        fieldErrorMessage,
        displayValue,
      })
    },
    [
      validationErrors,
      validationError,
      validationErrorFormat,
      forceError,
      showError,
      fieldState.error,
      fieldState.isDirty,
      clientValidationMessage,
      displayValue,
    ],
  )

  const applyValidationResult = useCallback((validationResult: ReturnType<LegacyFieldValidate>) => {
    if (validationResult !== true) {
      setClientValidationMessage(String(validationResult))
      scheduleShowError(true)
      return
    }
    setClientValidationMessage(null)
    scheduleShowError(false)
  }, [scheduleShowError])

  const afterValueChange = useCallback((nextValue: unknown) => {
    applyValidationResult(resolvedValidate(nextValue))
  }, [resolvedValidate, applyValidationResult])

  const afterBlur = useCallback((currentValue: unknown) => {
    if (errorOnPristine) {
      setShowError(true)
      return
    }
    applyValidationResult(resolvedValidate(currentValue))
  }, [errorOnPristine, resolvedValidate, applyValidationResult])

  return {
    field,
    fieldState,
    displayValue,
    externallyControlled,
    setValue,
    showError,
    setShowError,
    scheduleShowError,
    getDisplayedError,
    afterValueChange,
    afterBlur,
    validate: resolvedValidate,
  }
}
